import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsManuscriptReviewAIUrl } from "utils/urls";

function createRequestId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `manuscript-review-ai-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useManuscriptReviewAIWebSocket({
    spaceUuid,
    manuscriptId,
    enabled = true,
    onStarted,
    onDelta,
    onCompleted,
    onStopped,
    onError,
}) {
    const wsRef = useRef(null);
    const pendingMessageRef = useRef(null);
    const currentRequestIdRef = useRef(null);
    const [status, setStatus] = useState("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const connectionReady = useMemo(() => enabled && !!spaceUuid && !!manuscriptId, [enabled, manuscriptId, spaceUuid]);

    const handleSocketMessage = useCallback(
        (event) => {
            const data = JSON.parse(event.data || "{}");
            const type = data?.type;
            const payload = data?.payload || {};
            const requestId = payload?.request_id;
            if (requestId && requestId !== currentRequestIdRef.current) {
                return;
            }

            if (type === "review_ai.started") {
                setStatus("running");
                setErrorMessage("");
                onStarted?.(payload);
                return;
            }

            if (type === "review_ai.delta") {
                onDelta?.(payload);
                return;
            }

            if (type === "review_ai.completed") {
                setStatus("completed");
                onCompleted?.(payload);
                return;
            }

            if (type === "review_ai.stopped") {
                setStatus("stopped");
                onStopped?.(payload);
                return;
            }

            if (type === "review_ai.error") {
                setStatus("error");
                setErrorMessage(payload.message || "AI 리뷰 생성에 실패했습니다.");
                onError?.(payload);
            }
        },
        [onCompleted, onDelta, onError, onStarted, onStopped],
    );

    const connectWebSocket = useCallback(() => {
        if (!connectionReady || wsRef.current) {
            return;
        }

        const socket = new WebSocket(wsManuscriptReviewAIUrl(spaceUuid, manuscriptId));
        wsRef.current = socket;
        socket.onopen = () => {
            if (pendingMessageRef.current) {
                socket.send(JSON.stringify(pendingMessageRef.current));
                pendingMessageRef.current = null;
            }
        };
        socket.onmessage = handleSocketMessage;
        socket.onerror = () => {
            setErrorMessage("AI 리뷰 소켓 연결에 실패했습니다.");
        };
        socket.onclose = () => {
            wsRef.current = null;
        };
    }, [connectionReady, handleSocketMessage, manuscriptId, spaceUuid]);

    useEffect(() => {
        if (connectionReady) {
            connectWebSocket();
        }
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectWebSocket, connectionReady]);

    const sendOrQueue = useCallback(
        (message) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(message));
                return;
            }
            pendingMessageRef.current = message;
            connectWebSocket();
        },
        [connectWebSocket],
    );

    const startRequest = useCallback(
        ({ threadId, messageId }) => {
            const requestId = createRequestId();
            currentRequestIdRef.current = requestId;
            setStatus("connecting");
            setErrorMessage("");
            sendOrQueue({
                action: "start",
                payload: {
                    request_id: requestId,
                    thread_id: threadId,
                    message_id: messageId,
                },
            });
            return requestId;
        },
        [sendOrQueue],
    );

    const stopRequest = useCallback(() => {
        if (!currentRequestIdRef.current) {
            return;
        }
        sendOrQueue({
            action: "stop",
            payload: {
                request_id: currentRequestIdRef.current,
            },
        });
    }, [sendOrQueue]);

    return {
        status,
        errorMessage,
        isRunning: status === "running" || status === "connecting",
        startRequest,
        stopRequest,
    };
}
